import { useMemo, useState } from 'react';
import type { StyledTableColumn, StyledTableHeaderColumn } from './StyledTable';

export type SortOrder = 'asc' | 'desc';

/** A value a column can be sorted by. */
type SortValue = string | number | Date | null | undefined;

export interface SortableColumn<T> extends StyledTableColumn {
  /** Value this column is compared by. A column without it cannot be sorted. */
  sortBy?: (row: T) => SortValue;
}

export interface TableSortState {
  /** Name of the column being sorted by, null while the table is unsorted. */
  key: string | null;
  order: SortOrder;
  toggle: (key: string) => void;
}

/**
 * Columns whose heading mentions a date are compared as dates, everything else
 * with the default comparison — the rule the old app used. The heading decides,
 * not the value.
 */
const DATE_COLUMN = /datum|date/i;

const asTime = (value: SortValue): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

const isEmpty = (value: SortValue) => value === null || value === undefined || value === '';

const compare = (a: SortValue, b: SortValue, asDate: boolean): number => {
  if (asDate) {
    const left = asTime(a);
    const right = asTime(b);
    // Rows without a date go last, whichever way the column is sorted.
    if (left === null || right === null) return left === right ? 0 : left === null ? 1 : -1;
    return left - right;
  }

  if (isEmpty(a) || isEmpty(b)) return isEmpty(a) === isEmpty(b) ? 0 : isEmpty(a) ? 1 : -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;

  // numeric: true so "Los 2" sorts before "Los 10".
  return String(a).localeCompare(String(b), 'de', { numeric: true, sensitivity: 'base' });
};

/** Rows in the order the given column and direction ask for. Never mutates `rows`. */
export const sortRows = <T,>(columns: SortableColumn<T>[], rows: T[], key: string | null, order: SortOrder): T[] => {
  const column = columns.find((entry) => entry.name === key);
  if (!column?.sortBy) return rows;

  const { sortBy } = column;
  const asDate = DATE_COLUMN.test(column.name);
  const direction = order === 'asc' ? 1 : -1;

  return [...rows].sort((a, b) => direction * compare(sortBy(a), sortBy(b), asDate));
};

/**
 * Click-to-sort for a `StyledTable`. Declare the columns once with a `sortBy`
 * on each sortable one, then render the returned `rows` and hand `columns` and
 * `sort` to the table. The first click sorts descending, the next ascending.
 *
 * Pass `onReorder` for a table whose row order is itself the data — an edit
 * form that saves the position, for instance. The hook then leaves the rows
 * untouched and hands the new order to the caller to store, so that sorting
 * really rearranges the data instead of only the view.
 */
export const useTableSort = <T,>(columns: SortableColumn<T>[], rows: T[], onReorder?: (sorted: T[]) => void) => {
  const [key, setKey] = useState<string | null>(null);
  const [order, setOrder] = useState<SortOrder>('desc');

  const toggle = (name: string) => {
    const nextOrder: SortOrder = name === key && order === 'desc' ? 'asc' : 'desc';
    setKey(name);
    setOrder(nextOrder);
    onReorder?.(sortRows(columns, rows, name, nextOrder));
  };

  const sorted = useMemo(() => (onReorder ? rows : sortRows(columns, rows, key, order)), [columns, rows, key, order, onReorder]);

  const headerColumns: StyledTableHeaderColumn[] = useMemo(
    () => columns.map(({ sortBy, ...column }) => ({ ...column, sortable: Boolean(sortBy) })),
    [columns]
  );

  return { rows: sorted, columns: headerColumns, sort: { key, order, toggle } as TableSortState };
};
